import { ContentPane } from "@/components/ContentPane.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { useRef, useState } from "react";

/* eslint-disable react-hooks/refs */

interface FormDebuggerProps<T> {
    /** Initial form inputs */
    inputs?: Partial<T>;
    /** Callback when form inputs change */
    onChangeInputs?: (inputs: T) => void;
    /** The form component to render */
    children: (props: {
        key: number;
        inputs: Partial<T> | undefined;
        onChangeInputs: (inputs: T) => void;
    }) => React.ReactNode;
}

/**
 * Debugger component to check if re-loading a form works.
 * This is used to eg. retry creating a resource from the error notification.
 *
 * @template T - The type of the form inputs
 */
export function FormDebugger<T>({ inputs, onChangeInputs, children }: FormDebuggerProps<T>) {
    const currentInputsRef = useRef(inputs);
    const heldInputsRef = useRef(inputs);

    const [resetKey, setResetKey] = useState(0);

    return (
        <>
            {children({
                key: resetKey,
                inputs: heldInputsRef.current,
                onChangeInputs: (newInputs: T) => {
                    currentInputsRef.current = newInputs;
                    onChangeInputs?.(newInputs);
                },
            })}
            <ContentPane className="HStack gap-1">
                <MaskIcon className="size-0.875rem i-lucide:bug color-muted mr-1" />
                <WButton
                    variant="border"
                    label="Reload form"
                    icon="i-lucide:refresh-ccw"
                    action={() => {
                        console.log("Reloading form");
                        heldInputsRef.current = currentInputsRef.current;
                        setResetKey((k) => k + 1);
                    }}
                />
                <WButton
                    variant="border"
                    label="Log inputs"
                    action={() => {
                        console.log(currentInputsRef.current);
                    }}
                />
            </ContentPane>
        </>
    );
}
